import React, { useState } from "react";
import { Typography, Box } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { AppDispatch } from "../../../store";
import {
  selectUserFullName,
  selectUserInitials,
} from "../../../store/selectors/authSelectors";
import { logoutAction } from "../../../store/actions/authActions";
import { ROUTES } from "../../../constants";
import { AppLogoIcon, LogOutIcon } from "../../Icons";
import { Modal } from "../../Modal";
import { colors } from "../../../theme/colors";
import {
  SuperadminLayoutContainer,
  TopBar,
  LogoArea,
  UserArea,
  AvatarCircle,
  LogoutButton,
  ContentArea,
} from "./SuperadminLayout.styled";

type SuperadminLayoutProps = {
  children: React.ReactNode;
};

const SuperadminLayout: React.FC<SuperadminLayoutProps> = ({ children }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const userFullName = useSelector(selectUserFullName);
  const userInitials = useSelector(selectUserInitials);
  const [showLogoutModal, setShowLogoutModal] = useState(false);

  const handleLogoClick = () => {
    navigate(ROUTES.DASHBOARD);
  };

  const handleLogoutConfirm = async () => {
    try {
      await dispatch(logoutAction()).unwrap();
      navigate(ROUTES.LOGIN);
    } catch {
      navigate(ROUTES.LOGIN);
    } finally {
      setShowLogoutModal(false);
    }
  };

  return (
    <SuperadminLayoutContainer>
      <TopBar>
        <LogoArea onClick={handleLogoClick}>
          <AppLogoIcon height="36px" width="160px" />
        </LogoArea>

        <UserArea>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <AvatarCircle>{userInitials || "SA"}</AvatarCircle>
            <Box>
              <Typography
                sx={{
                  fontWeight: 700,
                  fontSize: 14,
                  color: colors.text.primary,
                  lineHeight: 1.2,
                  fontFamily: "Nunito",
                }}
              >
                {userFullName || t("common.superadmin")}
              </Typography>
              <Typography
                sx={{
                  fontSize: 12,
                  color: colors.text.secondary,
                  fontFamily: "Nunito",
                }}
              >
                {t("common.superadmin")}
              </Typography>
            </Box>
          </Box>

          <LogoutButton onClick={() => setShowLogoutModal(true)}>
            <LogOutIcon />
            <Typography
              sx={{
                fontSize: 14,
                fontFamily: "Nunito",
                color: "inherit",
              }}
            >
              {t("logoutConfirmation.confirm")}
            </Typography>
          </LogoutButton>
        </UserArea>
      </TopBar>

      <ContentArea>{children}</ContentArea>

      <Modal
        open={showLogoutModal}
        onClose={() => setShowLogoutModal(false)}
        title={t("logoutConfirmation.title")}
        primaryButtonText={t("logoutConfirmation.confirm")}
        secondaryButtonText={t("logoutConfirmation.cancel")}
        onPrimaryClick={handleLogoutConfirm}
        onSecondaryClick={() => setShowLogoutModal(false)}
      >
        <div />
      </Modal>
    </SuperadminLayoutContainer>
  );
};

export default SuperadminLayout;
